import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Modal, Spinner } from "react-bootstrap";
import { FilePlus2, Search, SlidersHorizontal, Trash2, X } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { PageHeader } from "../CMSDocuments/CmsDocumentComponents.js";
import { delete_article, get_articles } from "../../reduxData/cms/cmsAction";
import { formatDateTime } from "../../hooks/formatDateTime";
import EmptyList from "../../Shared/EmptyList";
import CustomPagination from "../../components/CustomPagination";
import { KB_CATEGORIES, KB_STATUS } from "./knowledgeBase.constants";
import "../../css/cmsDocuments.css";

const KnowledgeBaseList = () => {
  const dispatch = useDispatch();
  const { articles, totalArticles } = useSelector((state) => state.cms);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchArticles = () => get_articles(dispatch, currentPage, perPage, search.trim(), category, status);

  useEffect(() => {
    const timer = setTimeout(fetchArticles, 300);
    return () => clearTimeout(timer);
  }, [currentPage, perPage, search, category, status]);

  const clearFilters = () => {
    setSearch("");
    setCategory("");
    setStatus("");
    setCurrentPage(1);
  };

  const onDelete = async () => {
    setDeleting(true);
    const ok = await delete_article(dispatch, toDelete.id);
    if (ok) await fetchArticles();
    setDeleting(false);
    setToDelete(null);
  };

  return (
    <main className="category-page">
      <PageHeader title="Knowledge Base" description="Help articles shown to users.">
        <Link className="btn cmsd-primary" to="/cms/knowledge-base/add">
          <FilePlus2 size={18} /> Add Article
        </Link>
      </PageHeader>
      <section className="category-filter-card">
        <div className="category-filter-heading">
          <span className="category-filter-icon" aria-hidden="true">
            <SlidersHorizontal size={17} />
          </span>
          <div>
            <strong>Find articles</strong>
            <small>Results update automatically as you filter.</small>
          </div>
        </div>
        <div className="category-filter-controls kb-filter-controls">
          <div className="category-filter-search">
            <Search size={17} aria-hidden="true" />
            <input
              type="search"
              aria-label="Search articles"
              placeholder="Search article..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
            />
          </div>
          <label className="category-filter-field">
            <span>Category</span>
            <select value={category} onChange={(e) => { setCategory(e.target.value); setCurrentPage(1); }}>
              <option value="">All Categories</option>
              {KB_CATEGORIES.map((item) => (
                <option key={item} value={item}>{item}</option>
              ))}
            </select>
          </label>
          <label className="category-filter-field">
            <span>Status</span>
            <select value={status} onChange={(e) => { setStatus(e.target.value); setCurrentPage(1); }}>
              <option value="">All Status</option>
              <option value={KB_STATUS.PUBLISHED}>Published</option>
              <option value={KB_STATUS.DRAFT}>Draft</option>
            </select>
          </label>
          <button className="category-clear-filters" type="button" onClick={clearFilters}>
            <X size={16} aria-hidden="true" />
            Clear Filters
          </button>
        </div>
      </section>

      <div className="category-card">
        <div className="table-responsive">
          <table className="modern-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Title</th>
                <th>Category</th>
                <th>Status</th>
                <th>Author</th>
                <th>Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {articles.length > 0 ? (
                articles.map((article, index) => {
                  const published = String(article.status).toUpperCase() === KB_STATUS.PUBLISHED;
                  return (
                    <tr key={article.id}>
                      <td>{(currentPage - 1) * perPage + index + 1}</td>
                      <td>{article.title}</td>
                      <td>{article.category}</td>
                      <td>
                        <span className={published ? "status-active" : "status-inactive"}>
                          {published ? "Published" : "Draft"}
                        </span>
                      </td>
                      <td>{article.user_name || "—"}</td>
                      <td>{formatDateTime(article.updated_at)}</td>
                      <td>
                        <div className="table-actions">
                          <Link className="edit-btn" to={`/cms/knowledge-base/${article.id}/edit`}>
                            Edit
                          </Link>
                          <button
                            type="button"
                            className="category-status-toggle danger"
                            onClick={() => setToDelete(article)}
                          >
                            <span className="category-status-toggle-icon" aria-hidden="true">
                              <Trash2 size={11} />
                            </span>
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <EmptyList name="Articles" />
              )}
            </tbody>
          </table>
        </div>
      </div>

      {totalArticles > 0 && (
        <div className="pagination-wrapper category-pagination">
          <CustomPagination
            total={totalArticles}
            onPageChange={(page, rows) => { setCurrentPage(page); setPerPage(rows); }}
            resetPage={false}
          />
        </div>
      )}
      <Modal show={Boolean(toDelete)} centered onHide={deleting ? undefined : () => setToDelete(null)}>
        <Modal.Header closeButton={!deleting}>
          <Modal.Title>Delete article</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete "{toDelete?.title}"? This cannot be undone.</Modal.Body>
        <Modal.Footer>
          <button className="btn btn-light" disabled={deleting} onClick={() => setToDelete(null)}>Cancel</button>
          <button className="btn btn-danger" disabled={deleting} onClick={onDelete}>
            {deleting && <Spinner size="sm" className="me-2" />}Delete
          </button>
        </Modal.Footer>
      </Modal>
    </main>
  );
};

export default KnowledgeBaseList;
